import { ChangeDetectionStrategy, Component, ElementRef, afterNextRender, inject, viewChild } from "@angular/core";
import { Location } from "@angular/common";
import { Router, RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";

@Component({
  selector: "risr-not-found",
  imports: [MatButtonModule, MatIconModule, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        --surface: var(--color-white, #fff);
        --padding: calc(var(--base-padding, 0.64em) * 3);
        --space: var(--space-x, 0.64em);
        --radius: var(--border-radius, 4px);
        --card-shadow: var(--box-shadow, 0 2px 4px rgba(0, 0, 0, 0.1));
        --color-text: var(--sys-on-surface, #171717);
        --color-muted: var(--sys-on-surface-variant, #5f5f5f);

        background-color: var(--surface);
        border-radius: var(--radius);
        box-shadow: var(--card-shadow);
        color: var(--color-text);
        display: block;
        margin: calc(var(--space) * 2) auto;
        max-width: 42rem;
        padding: var(--padding);
        text-align: center;
      }

      .not-found__icon {
        color: var(--color-muted);
        font-size: 4rem;
        height: 4rem;
        width: 4rem;
      }

      .not-found__title {
        margin: var(--space) 0 0;
      }

      .not-found__title:focus {
        outline: none;
      }

      .not-found__message {
        color: var(--color-muted);
        margin: var(--space) 0 0;
      }

      .not-found__url {
        background-color: var(--sys-surface-variant, #f1f1f1);
        border-radius: var(--radius);
        display: inline-block;
        margin-top: var(--space);
        max-width: 100%;
        overflow-wrap: anywhere;
        padding: calc(var(--space) / 2) var(--space);
      }

      .not-found__actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space);
        justify-content: center;
        margin-top: calc(var(--space) * 2);
      }
    `,
  ],
  template: `
    <mat-icon class="not-found__icon" aria-hidden="true">search_off</mat-icon>

    <h1 #heading class="not-found__title" tabindex="-1" i18n="Not found|Page title">Page not found</h1>

    <p class="not-found__message" i18n="Not found|Explanation">
      The page you are looking for does not exist, has been moved, or you do not have permission to view it.
    </p>

    <p><code class="not-found__url">{{ attemptedUrl }}</code></p>

    <div class="not-found__actions">
      <a mat-flat-button color="primary" routerLink="/" i18n="Not found|Action">Go to dashboard</a>
      <button mat-stroked-button type="button" (click)="goBack()" i18n="Not found|Action">Go back</button>
    </div>
  `,
})
export class RisrNotFound {
  private router = inject(Router);
  private location = inject(Location);
  private heading = viewChild<ElementRef<HTMLElement>>("heading");

  protected readonly attemptedUrl = this.router.url;

  constructor() {
    afterNextRender(() => this.heading()?.nativeElement.focus());
  }

  protected goBack(): void {
    if (globalThis.history?.length > 1) {
      this.location.back();
      return;
    }
    this.router.navigate(["/"]);
  }
}
